import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { QrCodeDto } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/** Logo F or + bleu, fond transparent (`public/F-qr.png`). */
const QR_LOGO_SRC = "/F-qr.png";
const QR_DISPLAY_SIZE = 240;
const QR_DOWNLOAD_SIZE = 768;
/** Îlot central (~17 %) : assez petit pour ne pas manger le QR, assez grand pour lire le F. */
const QR_ISLAND_RATIO = 0.17;
const QR_ISLAND_INSET = 0.16;
/** Tokens canvas : `--brand-700` / `--accent` (`src/index.css`). */
const QR_FG_BRAND = "#2D3B50";
const QR_BG = "#ffffff";

const qrCanvasProps = {
  bgColor: QR_BG,
  fgColor: QR_FG_BRAND,
  level: "H" as const,
  includeMargin: true,
};

function drawLogoIsland(
  ctx: CanvasRenderingContext2D,
  qrSize: number,
  logo: HTMLImageElement
) {
  const island = qrSize * QR_ISLAND_RATIO;
  const radius = Math.max(2, island * 0.12);
  const x = (qrSize - island) / 2;
  const y = (qrSize - island) / 2;
  const inset = island * QR_ISLAND_INSET;

  ctx.save();
  ctx.fillStyle = QR_BG;
  ctx.beginPath();
  ctx.roundRect(x, y, island, island, radius);
  ctx.fill();
  ctx.drawImage(
    logo,
    x + inset,
    y + inset,
    island - inset * 2,
    island - inset * 2
  );
  ctx.restore();
}

const downloadFileName = (token: string) => {
  const safe = token.replace(/[^a-zA-Z0-9._-]+/g, "-").slice(0, 48);
  return `flash-pay-qr-${safe || "code"}.png`;
};

const pad2 = (value: number) => String(value).padStart(2, "0");

/** `HH:MM:SS`, ou `j HH:MM:SS` au-delà de 24 h. */
const formatRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const clock = `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
  return days > 0 ? `${days} j ${clock}` : clock;
};

/**
 * Temps restant jusqu'à `expiresAt`. L'intervalle repart quand le jeton ou
 * l'échéance change. `null` si le QR n'a pas de date d'expiration (STATIC).
 */
function useQrCountdown(token: string, expiresAt?: string | null) {
  const deadline = expiresAt?.trim() ?? "";
  const deadlineMs = deadline ? Date.parse(deadline) : Number.NaN;
  const active = Boolean(token) && Number.isFinite(deadlineMs);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active, token, deadline]);

  if (!active) return null;
  return {
    expired: deadlineMs <= now,
    label: formatRemaining(deadlineMs - now),
  };
}

/**
 * Prévisualisation du QR marchand. Le F est posé sur un petit îlot blanc
 * arrondi (or / bleu projet) : lisible, sans recouvrir ni altérer le reste
 * du motif. Niveau H pour rester scannable.
 */
export function QrCodePreviewDialog({
  qr,
  open,
  onOpenChange,
}: {
  qr: QrCodeDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const downloadCanvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const token = qr?.payloadToken?.trim() ?? "";
  const countdown = useQrCountdown(token, qr?.expiresAt);

  const copyToken = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      notifSilence(t("common.copied"));
    } catch {
      notifSilenceError(t("error"));
    }
  };

  const downloadQr = () => {
    const src = downloadCanvasRef.current;
    const logo = logoRef.current;
    if (!src || !logo || !token) return;

    const out = document.createElement("canvas");
    out.width = src.width;
    out.height = src.height;
    const ctx = out.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(src, 0, 0);
    drawLogoIsland(ctx, src.width, logo);
    out.toBlob((blob) => {
      if (!blob) {
        notifSilenceError(t("error"));
        return;
      }
      saveAs(blob, downloadFileName(token));
      notifSilence(tm("merchant.qrCodes.feedback.downloaded"));
    }, "image/png");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{tm("merchant.qrCodes.view.title")}</DialogTitle>
          <DialogDescription>
            {tm("merchant.qrCodes.view.description")}
          </DialogDescription>
        </DialogHeader>
        {token ? (
          <div className="flex flex-col items-center gap-4">
            <div className="relative inline-flex rounded-md border border-border bg-background p-4">
              <QRCodeCanvas
                key={token}
                value={token}
                size={QR_DISPLAY_SIZE}
                title={token}
                {...qrCanvasProps}
              />
              <div className="pointer-events-none absolute inset-4 flex items-center justify-center">
                <div
                  className="flex items-center justify-center rounded-sm bg-white p-[2.5%]"
                  style={{ width: `${QR_ISLAND_RATIO * 100}%`, height: `${QR_ISLAND_RATIO * 100}%` }}
                >
                  <img
                    ref={logoRef}
                    src={QR_LOGO_SRC}
                    alt=""
                    className="size-full object-contain"
                  />
                </div>
              </div>
            </div>
            <div className="pointer-events-none absolute h-0 w-0 overflow-hidden">
              <QRCodeCanvas
                key={token}
                ref={downloadCanvasRef}
                value={token}
                size={QR_DOWNLOAD_SIZE}
                {...qrCanvasProps}
              />
            </div>
            <NoTranslate
              as="span"
              className="break-all text-center font-mono text-xs text-muted-foreground"
            >
              {token}
            </NoTranslate>
            {countdown ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {countdown.expired
                    ? tm("merchant.qrCodes.view.expired")
                    : tm("merchant.qrCodes.view.countdown")}
                </span>
                <NoTranslate
                  as="span"
                  className="font-mono tabular-nums text-foreground"
                >
                  {countdown.label}
                </NoTranslate>
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {tm("merchant.qrCodes.view.missingToken")}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void copyToken()}
            disabled={!token}
          >
            {tm("merchant.qrCodes.actions.copyToken")}
          </Button>
          <Button type="button" onClick={downloadQr} disabled={!token}>
            <Download className="mr-2 h-4 w-4" />
            {tm("merchant.qrCodes.view.download")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
